import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  ShoppingCart as PurchaseOrderIcon,
  Business as SupplierIcon,
  LocalShipping as ReceiptIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import {
  getPurchaseOrders,
  getSuppliers,
  getInvoices,
  getAgingReport,
  formatCurrency,
  formatDate,
  getStatusColor
} from '../../services/procurementService';
import { StatusBadge } from '../ds';

function ProcurementDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    openPOs: 0,
    pendingReceipts: 0,
    pendingInvoices: 0,
    overduePayments: 0,
    totalOutstanding: 0
  });
  const [recentPOs, setRecentPOs] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [agingData, setAgingData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [suppliersRes, posRes, invoicesRes, agingRes] = await Promise.all([
        getSuppliers({ limit: 100 }),
        getPurchaseOrders({ limit: 10 }),
        getInvoices({ status: 'pending', limit: 5 }),
        getAgingReport()
      ]);

      // Handle different response structures from backend
      // Suppliers: { success, data: suppliers_array, pagination }
      // POs/Invoices: { success, data: { purchase_orders/invoices, total, ... } } or { success, data: [] }
      const suppliers = Array.isArray(suppliersRes?.data) ? suppliersRes.data : [];
      const posData = posRes?.data;
      const pos = Array.isArray(posData) ? posData : (posData?.purchase_orders || []);
      const invoicesData = invoicesRes?.data;
      const invoices = Array.isArray(invoicesData) ? invoicesData : (invoicesData?.invoices || []);
      const aging = agingRes?.data || {};

      // Calculate stats
      setStats({
        totalSuppliers: suppliers.length,
        activeSuppliers: suppliers.filter(s => s.is_active).length,
        // "Open" = anything not terminal. Accepts new `closed` and legacy `completed` / `received`.
        openPOs: pos.filter(po => !['closed', 'completed', 'received', 'cancelled'].includes(po.status)).length,
        // Pending receipts = sent-to-supplier but not yet received. After the
        // 2026-05-18 rename, `sent` is a timestamp (`sent_at`) on an `open` PO
        // rather than a status; we count both the new and legacy shape.
        pendingReceipts: pos.filter(po =>
          po.status === 'sent' || (po.status === 'open' && po.sent_at)
        ).length,
        pendingInvoices: invoices.length,
        overduePayments: aging.overdue_count || 0,
        totalOutstanding: aging.total_outstanding || 0
      });

      setRecentPOs(pos.slice(0, 5));
      setPendingInvoices(invoices.slice(0, 5));
      setAgingData(aging);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load procurement dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Procurement
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage suppliers, purchase orders, and payments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/procurement/purchase-orders/new')}
          >
            New Purchase Order
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadDashboardData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}
            onClick={() => navigate('/procurement/suppliers')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Active Suppliers</Typography>
                  <Typography variant="h4" fontWeight="bold">{stats.activeSuppliers}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    of {stats.totalSuppliers} total
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'primary.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SupplierIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}
            onClick={() => navigate('/procurement/purchase-orders')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Open POs</Typography>
                  <Typography variant="h4" fontWeight="bold">{stats.openPOs}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stats.pendingReceipts} awaiting delivery
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'info.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PurchaseOrderIcon sx={{ color: 'info.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}
            onClick={() => navigate('/procurement/invoices')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Pending Invoices</Typography>
                  <Typography variant="h4" fontWeight="bold">{stats.pendingInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    awaiting approval
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'warning.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <InvoiceIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' },
              borderLeft: stats.overduePayments > 0 ? '4px solid' : 'none',
              borderColor: 'error.main'
            }}
            onClick={() => navigate('/procurement/payments')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Outstanding</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(stats.totalOutstanding, agingData?.summary_currency || 'USD')}
                  </Typography>
                  {stats.overduePayments > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                      <Typography variant="caption" color="error">
                        {stats.overduePayments} overdue
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{
                  backgroundColor: stats.overduePayments > 0 ? 'error.light' : 'success.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PaymentIcon sx={{ color: stats.overduePayments > 0 ? 'error.main' : 'success.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Recent Purchase Orders */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">Recent Purchase Orders</Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/procurement/purchase-orders')}
                  size="small"
                >
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPOs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary" sx={{ py: 2 }}>
                            No purchase orders yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentPOs.map((po) => (
                        <TableRow
                          key={po.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                        >
                          <TableCell>
                            <Typography fontWeight="medium">{po.po_number}</Typography>
                          </TableCell>
                          <TableCell>{po.supplier_name}</TableCell>
                          <TableCell>{formatDate(po.po_date)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(po.total_amount, po.currency)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={po.status} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Aging Summary */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">Accounts Payable Aging</Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/procurement/payments')}
                  size="small"
                >
                  View Details
                </Button>
              </Box>
              {agingData ? (
                <Box>
                  {[
                    { label: 'Current (0-30 days)', value: (agingData.summary?.current?.amount || 0) + (agingData.summary?.['1-30']?.amount || 0), color: 'success' },
                    { label: '31-60 days', value: agingData.summary?.['31-60']?.amount || 0, color: 'info' },
                    { label: '61-90 days', value: agingData.summary?.['61-90']?.amount || 0, color: 'warning' },
                    { label: 'Over 90 days', value: agingData.summary?.['90+']?.amount || 0, color: 'error' }
                  ].map((bucket) => (
                    <Box key={bucket.label} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{bucket.label}</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(bucket.value, agingData.summary_currency || 'USD')}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={agingData.total_outstanding > 0
                          ? (bucket.value / agingData.total_outstanding) * 100
                          : 0
                        }
                        color={bucket.color}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                  <Box sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <Typography variant="subtitle1" fontWeight="600">Total Outstanding</Typography>
                    <Typography variant="subtitle1" fontWeight="600">
                      {formatCurrency(agingData.total_outstanding || 0, agingData.summary_currency || 'USD')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                  No aging data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Invoices */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">Invoices Awaiting Action</Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/procurement/invoices')}
                  size="small"
                >
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>PO Reference</TableCell>
                      <TableCell>Invoice Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="textSecondary" sx={{ py: 2 }}>
                            No pending invoices
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingInvoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/procurement/invoices/${invoice.id}`)}
                        >
                          <TableCell>
                            <Typography fontWeight="medium">{invoice.invoice_number}</Typography>
                          </TableCell>
                          <TableCell>{invoice.supplier_name}</TableCell>
                          <TableCell>{invoice.po_number || '—'}</TableCell>
                          <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                          <TableCell>{formatDate(invoice.due_date)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(invoice.total_amount, invoice.currency)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={invoice.status} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<SupplierIcon />}
                    onClick={() => navigate('/procurement/suppliers')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    Manage Suppliers
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PurchaseOrderIcon />}
                    onClick={() => navigate('/procurement/purchase-orders/new')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    Create Purchase Order
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ReceiptIcon />}
                    onClick={() => navigate('/procurement/goods-receipts')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    Record Goods Receipt
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PaymentIcon />}
                    onClick={() => navigate('/procurement/payments')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    Record Payment
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProcurementDashboard;
